import { useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, MapPin, Search, ShieldCheck, Sparkles } from 'lucide-react'
import {
  formatLossUSD,
  getReportYear,
  getStateInsights,
  listAllStates,
  nameToSlug,
} from '@/lib/ic3'
import { listStatePacks } from '@/states'

export const Route = createFileRoute('/states/')({
  component: StatesIndex,
})

const PILOT_SLUGS: ReadonlySet<string> = new Set(
  listStatePacks()
    .map(p => p.slug)
    .filter((s): s is string => Boolean(s)),
)

type SortKey = 'alpha' | 'losses' | 'percapita' | 'pilot'

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'alpha', label: 'Alphabetical' },
  { value: 'losses', label: 'Highest reported losses' },
  { value: 'percapita', label: 'Highest per-capita losses' },
  { value: 'pilot', label: 'Partner states first' },
]

function StatesIndex() {
  const states = listAllStates()
  const reportYear = getReportYear()
  const [query, setQuery] = useState('')
  const [sortBy, setSortBy] = useState<SortKey>('alpha')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return states
    return states.filter(
      ({ row, code }) =>
        row.name.toLowerCase().includes(q) ||
        code.toLowerCase().includes(q),
    )
  }, [states, query])

  const sortedFiltered = useMemo(() => {
    const arr = [...filtered]
    if (sortBy === 'losses') {
      arr.sort((a, b) => b.row.losses - a.row.losses)
    } else if (sortBy === 'percapita') {
      arr.sort((a, b) => {
        const ar = getStateInsights(a.code)?.lossesPerCapitaRank ?? 99
        const br = getStateInsights(b.code)?.lossesPerCapitaRank ?? 99
        return ar - br
      })
    } else if (sortBy === 'pilot') {
      arr.sort((a, b) => {
        const ap =
          PILOT_SLUGS.has(a.slug) || PILOT_SLUGS.has(a.code.toLowerCase())
        const bp =
          PILOT_SLUGS.has(b.slug) || PILOT_SLUGS.has(b.code.toLowerCase())
        if (ap === bp) return a.row.name.localeCompare(b.row.name)
        return ap ? -1 : 1
      })
    } else {
      arr.sort((a, b) => a.row.name.localeCompare(b.row.name))
    }
    return arr
  }, [filtered, sortBy])

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0a1330] text-white">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-14">
          <div className="flex items-center gap-2 text-sm text-violet-200">
            <MapPin className="h-4 w-4" />
            <span>Find your state</span>
          </div>
          <h1 className="mt-4 max-w-2xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            Fraud trends and reporting routes for every state.
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
            Data sourced from the FBI Internet Crime Complaint Center {reportYear} Annual Report.
            Every state has a free, locally-relevant page so residents can see what's
            actually happening where they live.
          </p>

          <dl className="mt-10 grid grid-cols-2 gap-x-8 gap-y-4 text-sm sm:grid-cols-4">
            <Stat label="States covered" value="50 + DC" />
            <Stat label="Source" value="FBI IC3" />
            <Stat label="Year" value={String(reportYear)} />
            <Stat label="Free for residents" value="Always" />
          </dl>
        </div>
      </section>

      {/* Search + sort */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-7">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label
              htmlFor="state-search"
              className="relative w-full sm:max-w-md"
            >
              <Search className="pointer-events-none absolute start-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                id="state-search"
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search states by name…"
                className="w-full rounded-2xl border border-slate-200 bg-white py-3.5 ps-12 pe-4 text-base text-slate-900 placeholder:text-slate-400 focus:border-violet-300 focus:outline-none focus:ring-4 focus:ring-violet-100"
                autoComplete="off"
              />
            </label>

            <div className="flex items-center gap-2 sm:ms-auto">
              <label
                htmlFor="state-sort"
                className="text-sm font-medium text-slate-600"
              >
                Sort
              </label>
              <select
                id="state-sort"
                value={sortBy}
                onChange={e => setSortBy(e.target.value as SortKey)}
                className="cursor-pointer rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 focus:border-violet-300 focus:outline-none focus:ring-4 focus:ring-violet-100"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="mt-3 text-xs text-slate-500">
            {query
              ? `Showing ${sortedFiltered.length} of ${states.length} states`
              : `${states.length} states and territories`}
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-7xl px-6 py-14">
        {sortedFiltered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center">
            <p className="text-sm font-semibold text-slate-700">
              No states match “{query}”.
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Try a different name or abbreviation.
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sortedFiltered.map(({ code, slug, row }) => {
            const insights = getStateInsights(code)
            const isPilot =
              PILOT_SLUGS.has(slug) || PILOT_SLUGS.has(code.toLowerCase())
            return (
              <Link
                key={code}
                to="/states/$slug"
                params={{ slug }}
                className={`group relative flex items-center gap-4 overflow-hidden rounded-2xl border bg-white p-4 transition-all ${
                  isPilot
                    ? 'border-violet-200 hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-[0_18px_40px_-18px_rgba(15,15,40,0.18)]'
                    : 'border-slate-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_18px_40px_-18px_rgba(15,15,40,0.12)]'
                }`}
              >
                {/* Flag */}
                <div
                  className={`relative h-12 w-16 shrink-0 overflow-hidden rounded-md ring-1 ring-inset ${
                    isPilot ? 'ring-violet-200' : 'ring-slate-200/80'
                  }`}
                >
                  <img
                    src={`/states/flags/${code.toLowerCase()}.svg`}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </div>

                {/* Body */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-[15px] font-bold tracking-tight text-slate-900">
                      {row.name}
                    </h3>
                    {isPilot && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 ring-1 ring-violet-100">
                        <Sparkles className="h-3 w-3" />
                        Pilot
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-600">
                    <span className="tabular font-semibold text-slate-900">
                      {formatLossUSD(row.losses)}
                    </span>
                    <span className="text-slate-500"> reported</span>
                    <span className="mx-1.5 text-slate-300">·</span>
                    <span className="text-slate-500">
                      #{row.lossesRank} total
                    </span>
                    {insights && (
                      <>
                        <span className="mx-1.5 text-slate-300">·</span>
                        <span className="text-slate-500">
                          #{insights.lossesPerCapitaRank} per capita
                        </span>
                      </>
                    )}
                  </p>
                </div>

                <ArrowRight
                  className={`h-4 w-4 shrink-0 self-center transition-all ${
                    isPilot
                      ? 'text-violet-300 group-hover:translate-x-0.5 group-hover:text-violet-600'
                      : 'text-slate-300 group-hover:translate-x-0.5 group-hover:text-violet-500'
                  }`}
                />
              </Link>
            )
          })}
        </div>
        )}
      </section>

      {/* Partner CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-violet-50/40 p-8 sm:p-10">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-[1.4fr_1fr] sm:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
                <ShieldCheck className="h-3.5 w-3.5" />
                For state agencies and institutions
              </div>
              <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                Customize your state's page.
              </h2>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-600">
                Add your branding, your hotline, and examples that reflect the
                scams your residents face. Same template, your voice.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:justify-end">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Talk to us
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/states/$slug"
                params={{ slug: 'maryland' }}
                search={{ tenant: 'md-treasury' } as never}
                className="text-sm font-semibold text-violet-700 underline-offset-4 hover:underline"
              >
                See a sponsored example →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase text-violet-200/80">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tracking-tight">{value}</dd>
    </div>
  )
}
